/**
 * InsuranceInfoSection
 *
 * Collapsible insurance details using InlineEditableRow rows. White background,
 * bold labels with values underneath to match the checklist visual language.
 */
import { BookOpen } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { CarrierPocketbook } from '@/components/carrier-directory';
import { CarrierNameSelect } from '@/components/carrier-directory/CarrierNameSelect';
import { DirectoryHandlesPreview } from '@/components/carrier-directory/DirectoryHandlesPreview';
import { EditableFieldRow } from '@/components/claims-filing/common/EditableFieldRow';
import {
  usePromoteLorSelectionsMutation,
  useUpdateCoverageMutation,
} from '@/services/api/coverageService';
import {
  CoverageSchema,
  IdentifierType,
  PolicyIdentifier,
} from '@/services/types/coverage-types';

import { DisclosureSection } from './DisclosureSection';
import { FieldSection } from './FieldSection';

interface InsuranceInfoSectionProps {
  matterId: string;
  coverage: CoverageSchema;
  onHoverField?: (key: string | null) => void;
}

export const InsuranceInfoSection: React.FC<InsuranceInfoSectionProps> = ({
  matterId,
  coverage,
  onHoverField,
}) => {
  const [updateCoverage] = useUpdateCoverageMutation();
  const [promoteLorSelections] = usePromoteLorSelectionsMutation();

  const [carrierName, setCarrierName] = useState(coverage.carrier?.name || '');
  const [insuredName, setInsuredName] = useState(
    (coverage.named_insured || []).join(', '),
  );

  const policyIdentifiers = (coverage.policy_identifiers ||
    []) as PolicyIdentifier[];
  const primaryPolicyNumber = useMemo(() => {
    const policy = policyIdentifiers.find(
      (p) => p.identifier_type === IdentifierType.POLICY_NUMBER,
    );
    return (
      policy?.identifier_value || policyIdentifiers[0]?.identifier_value || ''
    );
  }, [policyIdentifiers]);
  const [policyNumberState, setPolicyNumberState] =
    useState<string>(primaryPolicyNumber);

  const [isOpen, setIsOpen] = useState<boolean>(true);

  // Get carrier contact handles
  const carrierContacts = coverage.carrier?.handles || [];
  const hasContacts = carrierContacts.length > 0;

  return (
    <DisclosureSection
      title="Insurance Information"
      isOpen={isOpen}
      onToggle={() => setIsOpen((v) => !v)}
      headerActions={
        <CarrierPocketbook
          trigger={
            <button
              type="button"
              className="flex items-center gap-1.5 px-3 py-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors border border-blue-200 hover:border-blue-300"
              title="Add Carrier Contact"
            >
              <BookOpen className="w-4.5 h-4.5" />
              <span className="text-sm font-medium">Add Carrier Contact</span>
            </button>
          }
          onSelect={() => {
            // Keep copy action available for handles
          }}
          onSelectDivision={async (carrier, divisionId) => {
            try {
              await updateCoverage({
                matterId,
                coverageId: coverage.id,
                coverage: {
                  carrier_v2_id: carrier.id,
                  carrier_division_id: divisionId,
                } as any,
              }).unwrap();
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error('Failed to apply carrier division', err);
            }
          }}
          defaultSelectedCarrierId={
            (coverage as unknown as { carrier_v2_id?: string | null })
              .carrier_v2_id || undefined
          }
        />
      }
    >
      <FieldSection className="border-0">
        <div className="divide-y">
          {!hasContacts && (
            <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3">
              <div className="text-sm font-semibold text-red-800">
                Missing carrier contact
              </div>
              <div className="text-xs text-red-700 mt-1">
                Attach a carrier contact handle (phone, email, fax, website) to
                this carrier. Use the Carrier Directory to select the correct
                division and handles.
              </div>
            </div>
          )}
          <div className="px-3 py-2">
            <div className="text-xs font-semibold text-gray-700 mb-1">
              Insurance Co Name
              <span className="text-red-500 ml-1">*</span>
            </div>
            <div className="flex items-center gap-2">
              <CarrierNameSelect
                value={carrierName}
                onChange={async (name) => {
                  setCarrierName(name);
                  try {
                    // Build the update payload
                    const coverageUpdate: Record<string, unknown> = {
                      carrier: {
                        name,
                        handles: coverage.carrier?.handles || null,
                        address: coverage.carrier?.address || null,
                      },
                    };
                    // When carrier name is cleared, also unlink the carrier directory references
                    if (!name) {
                      coverageUpdate.carrier_v2_id = null;
                      coverageUpdate.carrier_division_id = null;
                    }
                    await updateCoverage({
                      matterId,
                      coverageId: coverage.id,
                      coverage: coverageUpdate as any,
                    }).unwrap();
                  } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('Failed to update carrier name', err);
                  }
                }}
                onSelectDirectory={async (carrierId, divisionId) => {
                  try {
                    await updateCoverage({
                      matterId,
                      coverageId: coverage.id,
                      coverage: {
                        carrier_v2_id: carrierId,
                        carrier_division_id: divisionId || null,
                      } as any,
                    }).unwrap();
                  } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error(
                      'Failed to update carrier directory selection',
                      err,
                    );
                  }
                }}
              />
            </div>
          </div>
          <div className="flex items-center justify-end py-2">
            <CarrierPocketbook
              trigger={
                <button
                  type="button"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors border border-blue-200 hover:border-blue-300"
                  title="Add Carrier Contact"
                >
                  <BookOpen className="w-4.5 h-4.5" />
                  <span className="text-sm font-medium">
                    Add Carrier Contact
                  </span>
                </button>
              }
              onAttachSelected={(carrier, selected) => {
                // Set carrier name and attach selected handles
                setCarrierName(carrier.name);
                const toValue = (h: any): string => {
                  if (h.value) return h.value;
                  const parts = [
                    h.addressLine1,
                    h.city,
                    h.state,
                    h.zip,
                    h.country,
                  ].filter(Boolean);
                  return parts.join(', ');
                };
                const mapped =
                  selected?.map((h: any) => ({
                    handle_type: h.handleType,
                    value: toValue(h),
                    name: h.name,
                  })) || [];
                const existing = coverage.carrier?.handles || [];
                updateCoverage({
                  matterId,
                  coverageId: coverage.id,
                  coverage: {
                    carrier: {
                      name: carrier.name,
                      handles: [...existing, ...mapped],
                      address: coverage.carrier?.address || null,
                    } as any,
                  },
                });
              }}
            />
          </div>
          <EditableFieldRow
            id="ins:insured"
            label="Insured Name"
            value={insuredName}
            required
            hoverKey="ins:insured"
            onHover={onHoverField}
            onChange={setInsuredName}
            onSave={async () => {
              try {
                await updateCoverage({
                  matterId,
                  coverageId: coverage.id,
                  coverage: {
                    named_insured: insuredName ? [insuredName] : [],
                  },
                }).unwrap();
              } catch (err) {
                // eslint-disable-next-line no-console
                console.error('Failed to update insured name', err);
              }
            }}
            onCancel={() =>
              setInsuredName((coverage.named_insured || []).join(', '))
            }
          />
          <EditableFieldRow
            id="ins:policy"
            label="Policy Number"
            value={policyNumberState}
            required
            hoverKey="ins:policy"
            onHover={onHoverField}
            onChange={setPolicyNumberState}
            onSave={async () => {
              try {
                await promoteLorSelections({
                  matterId,
                  coverageId: coverage.id,
                  policyNumber: policyNumberState,
                }).unwrap();
              } catch (err) {
                // eslint-disable-next-line no-console
                console.error('Failed to update policy number', err);
              }
            }}
            onCancel={() => setPolicyNumberState(primaryPolicyNumber)}
          />

          {/* Carrier Contact Information - Read-only display (directory selection preferred) */}
          {(() => {
            const cId = (coverage as any).carrier_v2_id as string | undefined;
            const dId = (coverage as any).carrier_division_id as
              | string
              | undefined;
            return cId ? (
              <DirectoryHandlesPreview
                carrierId={cId}
                divisionId={dId}
                title="Carrier Contacts (Directory)"
                className="px-3 py-2"
              />
            ) : null;
          })()}
          {!(coverage as any).carrier_v2_id &&
            hasContacts &&
            carrierContacts.map((contact, index) => {
              const contactType =
                contact.handle_type?.toLowerCase() || 'contact';
              const displayLabel =
                contact.name ||
                (contactType === 'phone'
                  ? 'Insurance Phone'
                  : contactType === 'email'
                    ? 'Insurance Email'
                    : contactType === 'website'
                      ? 'Insurance Website'
                      : contactType === 'fax'
                        ? 'Insurance Fax'
                        : `Insurance ${contactType.charAt(0).toUpperCase() + contactType.slice(1)}`);

              // Format contact value based on type
              const formatContactValue = (value: string, type: string) => {
                if (type === 'phone' || type === 'fax') {
                  // Format phone numbers for better readability
                  const cleaned = value.replace(/\D/g, '');
                  if (cleaned.length === 10) {
                    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
                  }
                  if (cleaned.length === 11 && cleaned[0] === '1') {
                    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
                  }
                }
                return value;
              };

              return (
                <div
                  key={`contact-${index}`}
                  className="px-3 py-2 hover:bg-indigo-50/30 transition-colors group"
                  onMouseEnter={() => onHoverField?.(`ins:contact-${index}`)}
                  onFocus={() => onHoverField?.(`ins:contact-${index}`)}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-semibold text-gray-900 truncate">
                        {displayLabel}
                      </span>
                      <span className="text-[10px] uppercase tracking-wide text-gray-500">
                        Read-only
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 text-sm font-medium text-gray-900 truncate">
                    {formatContactValue(contact.value, contactType)}
                  </div>
                </div>
              );
            })}
        </div>
      </FieldSection>
    </DisclosureSection>
  );
};
